import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/layout/layout';
import { useLanguage } from './hooks/useLanguage';
import { useTheme } from './providers/theme-provider';
import { RTLWrapper } from './components/ui/rtl-wrapper';
import { ResponsiveProvider } from './providers/ResponsiveProvider';
import { AccessibilityProvider } from './components/accessibility/AccessibilityProvider';
import { SkipNavigation } from './components/accessibility/SkipNavigation';
import { LiveRegion } from './components/accessibility/LiveRegion';
import { MessageNotificationProvider } from './components/notifications/MessageNotificationProvider';
import { FloatingMessageProvider } from './components/messages/floating-message-provider';
import { AIAssistantProvider } from './components/ai-assistant/AIAssistantProvider';
import { CallProvider } from './components/calls/CallProvider';
import { LightboxProvider } from './hooks/useLightbox';
import { GlobalLightbox } from './components/ui/global-lightbox';
import { NetworkStatusBanner } from './components/ui/network-status-banner';
import { preloadLikelyRoutes, routes } from './routes';
import { useAuth } from './providers/AuthProvider';
import { useNavigationHistoryTracker } from './hooks/useNavigationHistory';

const CallInterface = React.lazy(() =>
  import('./components/calls/CallInterface').then((m) => ({ default: m.CallInterface }))
);
const IncomingCallNotification = React.lazy(() =>
  import('./components/calls/IncomingCallNotification').then((m) => ({ default: m.IncomingCallNotification }))
);

function RouteLoadingFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="text-slate-600 font-medium">Loading…</span>
      </div>
    </div>
  );
}

function runDeferredTask(action: () => void, delayMs: number, idleTimeoutMs: number): () => void {
  let idleId: number | null = null;

  const timeoutId = window.setTimeout(() => {
    if ('requestIdleCallback' in window) {
      idleId = (window as Window & { requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number }).requestIdleCallback(
        () => action(),
        { timeout: idleTimeoutMs }
      );
      return;
    }

    action();
  }, delayMs);

  return () => {
    window.clearTimeout(timeoutId);
    if (idleId !== null && 'cancelIdleCallback' in window) {
      (window as Window & { cancelIdleCallback: (handle: number) => void }).cancelIdleCallback(idleId);
    }
  };
}

function App() {
  useLanguage();
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const { user } = useAuth();
  useNavigationHistoryTracker(user?.uid);

  const callRouteActive = useMemo(
    () => /^\/(messages|friends|calls)(\/|$)/i.test(pathname),
    [pathname]
  );

  const callProviderEnabled = callRouteActive;

  useEffect(() => {
    return runDeferredTask(() => preloadLikelyRoutes(), 1200, 5000);
  }, []);

  const shellContent = (
    <LightboxProvider>
      <AIAssistantProvider>
        <RTLWrapper>
          <div className={`app-container intouch-app-shell min-h-screen ${theme === 'dark' ? 'dark' : ''} text-foreground transition-colors duration-300`}>
            <SkipNavigation />
            <LiveRegion />
            <Layout>
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  {routes.map(({ path, component: Component }) => (
                    <Route
                      key={path}
                      path={path}
                      element={<Component />}
                    />
                  ))}
                </Routes>
              </Suspense>
            </Layout>

            <Suspense fallback={null}>
              <CallInterface />
              <IncomingCallNotification />
            </Suspense>

            <GlobalLightbox />
            <NetworkStatusBanner />
          </div>
        </RTLWrapper>
      </AIAssistantProvider>
    </LightboxProvider>
  );

  const withFloatingMessages = (
    <FloatingMessageProvider>{shellContent}</FloatingMessageProvider>
  );

  const appContent = (
    <MessageNotificationProvider>{withFloatingMessages}</MessageNotificationProvider>
  );

  return (
    <AccessibilityProvider>
      <ResponsiveProvider>
        <CallProvider enabled={callProviderEnabled}>{appContent}</CallProvider>
      </ResponsiveProvider>
    </AccessibilityProvider>
  );
}

export default App;
